import { useState, useEffect } from 'react';
import { useAuth, CONNECTION_STATUS } from '../context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Activity, Mail, Lock, User, AlertCircle, CheckCircle, AlertTriangle, ArrowLeft,
  Target, TrendingUp, BarChart3, Zap, Shield, Bell, Loader2, RefreshCw, Wifi, WifiOff
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Feature highlights for the login page
const FEATURES = [
  { icon: Target, title: 'Stock Scanner', desc: 'Entry zones, fair value & profit targets' },
  { icon: TrendingUp, title: 'Options Flow', desc: 'CALL/PUT opportunities auto-detected' },
  { icon: BarChart3, title: 'Trade Scoring', desc: 'A+ setups ranked by confluence' },
  { icon: Zap, title: 'Auto Trading', desc: 'Paper trade A+ setups automatically' },
  { icon: Shield, title: 'Risk Analysis', desc: 'Market regime & volatility tracking' },
  { icon: Bell, title: 'Smart Alerts', desc: 'Price, sentiment & OI change alerts' },
];

export default function AuthPage({ onBack }) {
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [signupSuccess, setSignupSuccess] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [resetToken, setResetToken] = useState(null);
  const [resetEmail, setResetEmail] = useState('');
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [error, setError] = useState('');
  
  const { login, signup, serverStatus, connectionAttempts, retryConnection, isServerConnected, isServerConnecting, enterDemoMode } = useAuth();

  // Check for reset token in URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('reset_token');
    if (token) {
      verifyResetToken(token);
    }
  }, []);

  const verifyResetToken = async (token) => {
    try {
      const response = await axios.get(`${API}/auth/verify-reset-token/${token}`);
      setResetToken(token);
      setResetEmail(response.data.email);
      setShowForgotPassword(true);
      // Clear the URL parameter
      window.history.replaceState({}, '', window.location.pathname);
    } catch (err) {
      toast.error('Invalid or expired reset link');
      window.history.replaceState({}, '', window.location.pathname);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isLogin) {
        await login(email, password);
        toast.success('Welcome back!');
      } else {
        await signup(email, password, fullName);
        setSignupSuccess(true);
        toast.success('Registration successful!');
      }
    } catch (err) {
      let message = 'An error occurred';
      
      if (err.code === 'ERR_NETWORK' || err.code === 'ECONNABORTED') {
        message = 'Connection issue. Please wait while we reconnect...';
        // Trigger retry connection
        retryConnection();
      } else if (err.response?.status === 500) {
        message = 'Server error. Please try again in a moment.';
      } else if (err.response?.status === 502 || err.response?.status === 503) {
        message = 'Server is warming up. Retrying automatically...';
        // Trigger retry connection
        retryConnection();
      } else if (err.response?.data?.detail) {
        message = err.response.data.detail;
      } else if (err.message) {
        message = err.message;
      }
      
      setError(message);
      toast.error(message);
    }
    
    setLoading(false);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setResetEmailSent(true);
      toast.success('Reset instructions sent to your email');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send reset email');
    }
    setLoading(false);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token: resetToken,
        new_password: password
      });
      toast.success('Password reset successful! Please login.');
      setShowForgotPassword(false);
      setResetToken(null);
      setPassword('');
      setConfirmPassword('');
      setIsLogin(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    }
    setLoading(false);
  };

  // Signup success screen
  if (signupSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-emerald-600" />
          </div>
          <h2 className="text-2xl font-bold text-zinc-900 mb-2">Registration Successful!</h2>
          <p className="text-zinc-600 mb-6">
            Your account is pending admin approval. You'll be able to login once approved.
          </p>
          <Button 
            onClick={() => {
              setSignupSuccess(false);
              setIsLogin(true);
              setEmail('');
              setPassword('');
              setFullName('');
            }}
            className="w-full bg-blue-600 hover:bg-blue-700"
          >
            Back to Login
          </Button>
        </div>
      </div>
    );
  }

  // Password reset - enter new password (when token is valid)
  if (showForgotPassword && resetToken) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-blue-600" />
            </div>
            <h2 className="text-2xl font-bold text-zinc-900">Set New Password</h2>
            <p className="text-zinc-500 text-sm mt-1">for {resetEmail}</p>
          </div>
          
          <form onSubmit={handleResetPassword} className="space-y-4">
            <div>
              <label className="text-sm text-zinc-600 mb-1 block">New Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 w-4 h-4 text-zinc-400" />
                <Input
                  type="password"
                  placeholder="Enter new password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10"
                  required
                />
              </div>
            </div>
            
            <div>
              <label className="text-sm text-zinc-600 mb-1 block">Confirm Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 w-4 h-4 text-zinc-400" />
                <Input
                  type="password"
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="pl-10"
                  required
                />
              </div>
            </div>
            
            <Button 
              type="submit" 
              className="w-full bg-blue-600 hover:bg-blue-700"
              disabled={loading}
            >
              {loading ? 'Resetting...' : 'Reset Password'}
            </Button>
          </form>
          
          <button
            onClick={() => {
              setShowForgotPassword(false);
              setResetToken(null);
              setPassword('');
              setConfirmPassword('');
            }}
            className="w-full text-sm text-zinc-500 hover:text-zinc-700 mt-4 flex items-center justify-center gap-1"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Login
          </button>
        </div>
      </div>
    );
  }

  // Password reset - email sent confirmation
  if (showForgotPassword && resetEmailSent) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Mail className="w-8 h-8 text-emerald-600" />
          </div>
          <h2 className="text-2xl font-bold text-zinc-900 mb-2">Check Your Email</h2>
          <p className="text-zinc-600 mb-6">
            We've sent password reset instructions to <strong>{email}</strong>
          </p>
          <p className="text-sm text-zinc-500 mb-6">
            Didn't receive the email? Check your spam folder or try again.
          </p>
          <div className="space-y-2">
            <Button 
              onClick={() => {
                setResetEmailSent(false);
              }}
              variant="outline"
              className="w-full"
            >
              Try Again
            </Button>
            <Button 
              onClick={() => {
                setShowForgotPassword(false);
                setResetEmailSent(false);
                setEmail('');
              }}
              className="w-full bg-blue-600 hover:bg-blue-700"
            >
              Back to Login
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Password reset - enter email
  if (showForgotPassword) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-amber-600" />
            </div>
            <h2 className="text-2xl font-bold text-zinc-900">Forgot Password?</h2>
            <p className="text-zinc-500 text-sm mt-1">Enter your email to receive reset instructions</p>
          </div>
          
          <form onSubmit={handleForgotPassword} className="space-y-4">
            <div className="relative">
              <Mail className="absolute left-3 top-3 w-4 h-4 text-zinc-400" />
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pl-10"
                required
              />
            </div>
            
            <Button 
              type="submit" 
              className="w-full bg-blue-600 hover:bg-blue-700"
              disabled={loading}
            >
              {loading ? 'Sending...' : 'Send Reset Link'}
            </Button>
          </form>
          
          <button
            onClick={() => {
              setShowForgotPassword(false);
              setEmail('');
            }}
            className="w-full text-sm text-zinc-500 hover:text-zinc-700 mt-4 flex items-center justify-center gap-1"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Login
          </button>
        </div>
      </div>
    );
  }

  // Main login/signup form
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center p-4">
      <div className="max-w-4xl w-full flex flex-col lg:flex-row gap-6 lg:gap-8">
        
        {/* Left Side - Features (hidden on small screens when signing up) */}
        <div className={`lg:flex-1 ${!isLogin ? 'hidden lg:block' : ''}`}>
          <div className="text-center lg:text-left mb-6">
            <div className="flex items-center justify-center lg:justify-start gap-2 mb-2">
              <Activity className="w-10 h-10 text-cyan-400" />
              <h1 className="text-3xl font-bold text-white">TradeRelax</h1>
            </div>
            <p className="text-zinc-400">Professional Trading Dashboard for Smart Investors</p>
          </div>
          
          {/* Feature Grid */}
          <div className="grid grid-cols-2 gap-3 mb-6">
            {FEATURES.map((feature, idx) => (
              <div 
                key={idx}
                className="bg-white/5 backdrop-blur border border-white/10 rounded-xl p-3 hover:bg-white/10 transition-colors"
              >
                <feature.icon className="w-5 h-5 text-cyan-400 mb-2" />
                <h3 className="text-sm font-semibold text-white">{feature.title}</h3>
                <p className="text-xs text-zinc-400">{feature.desc}</p>
              </div>
            ))}
          </div>
          
          {/* Trust Badge */}
          <div className="hidden lg:flex items-center gap-3 text-zinc-400 text-sm">
            <CheckCircle className="w-5 h-5 text-emerald-400" />
            <span>Real-time market data • AI-powered insights • Paper trading</span>
          </div>
        </div>
        
        {/* Right Side - Auth Form */}
        <div className="lg:w-96">
          <div className="bg-white rounded-xl shadow-2xl p-6 sm:p-8">
            {/* Mobile Header (only shown on mobile) */}
            <div className="lg:hidden text-center mb-6">
              <div className="flex items-center justify-center gap-2 mb-1">
                <Activity className="w-7 h-7 text-blue-600" />
                <h1 className="text-xl font-bold text-zinc-900">TradeRelax</h1>
              </div>
              <p className="text-zinc-500 text-xs">Professional Trading Dashboard</p>
            </div>
            
            {/* Desktop Header */}
            <div className="hidden lg:block text-center mb-6">
              <h2 className="text-xl font-bold text-zinc-900">
                {isLogin ? 'Welcome Back' : 'Create Account'}
              </h2>
              <p className="text-zinc-500 text-sm">
                {isLogin ? 'Sign in to your dashboard' : 'Start your trading journey'}
              </p>
            </div>

            {/* Server Connection Status */}
            {isServerConnecting && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg" data-testid="server-connecting">
                <div className="flex items-center gap-3">
                  <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
                  <div className="flex-1">
                    <p className="text-blue-800 font-medium text-sm">Connecting to server...</p>
                    <p className="text-blue-600 text-xs">
                      {connectionAttempts > 1 
                        ? `Attempt ${connectionAttempts}/15 - Server is warming up` 
                        : 'Please wait a moment'}
                    </p>
                  </div>
                </div>
                {connectionAttempts > 3 && (
                  <div className="mt-2 w-full bg-blue-200 rounded-full h-1.5">
                    <div 
                      className="bg-blue-600 h-1.5 rounded-full transition-all duration-500" 
                      style={{ width: `${Math.min((connectionAttempts / 15) * 100, 100)}%` }}
                    />
                  </div>
                )}
              </div>
            )}

            {serverStatus === CONNECTION_STATUS.FAILED && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg" data-testid="server-failed">
                <div className="flex items-center gap-3">
                  <WifiOff className="w-5 h-5 text-red-600" />
                  <div className="flex-1">
                    <p className="text-red-800 font-medium text-sm">Unable to connect</p>
                    <p className="text-red-600 text-xs">Server may be restarting</p>
                  </div>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={retryConnection}
                    className="border-red-300 text-red-600 hover:bg-red-100"
                  >
                    <RefreshCw className="w-3 h-3 mr-1" />
                    Retry
                  </Button>
                </div>
              </div>
            )}

            {isServerConnected && connectionAttempts > 1 && (
              <div className="mb-4 p-2 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2" data-testid="server-connected">
                <Wifi className="w-4 h-4 text-green-600" />
                <span className="text-green-700 text-xs">Connected to server</span>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span className="text-xs">{error}</span>
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-3">
              {!isLogin && (
                <div className="relative">
                  <User className="absolute left-3 top-3 w-4 h-4 text-zinc-400" />
                  <Input
                    type="text"
                    placeholder="Full Name"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    className="pl-10"
                    required={!isLogin}
                    data-testid="fullname-input"
                  />
                </div>
              )}
              
              <div className="relative">
                <Mail className="absolute left-3 top-3 w-4 h-4 text-zinc-400" />
                <Input
                  type="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10"
                  required
                  data-testid="email-input"
                />
              </div>
              
              <div className="relative">
                <Lock className="absolute left-3 top-3 w-4 h-4 text-zinc-400" />
                <Input
                  type="password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10"
                  required
                  data-testid="password-input"
                />
              </div>

              <Button 
                type="submit" 
                className="w-full bg-blue-600 hover:bg-blue-700"
                disabled={loading || isServerConnecting || serverStatus === CONNECTION_STATUS.FAILED}
                data-testid="auth-submit"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Signing in...
                  </span>
                ) : isServerConnecting ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Connecting...
                  </span>
                ) : (isLogin ? 'Sign In' : 'Create Account')}
              </Button>

              {isLogin && (
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="w-full text-sm text-blue-600 hover:underline"
                  data-testid="forgot-password-btn"
                >
                  Forgot Password?
                </button>
              )}
            </form>

            {/* Toggle */}
            <div className="mt-4 text-center">
              <p className="text-sm text-zinc-600">
                {isLogin ? "Don't have an account?" : "Already have an account?"}
                <button
                  type="button"
                  onClick={() => {
                    setIsLogin(!isLogin);
                    setError('');
                  }}
                  className="ml-1 text-blue-600 hover:underline font-medium"
                  data-testid="auth-toggle"
                >
                  {isLogin ? 'Sign Up' : 'Sign In'}
                </button>
              </p>
            </div>

            {/* Try Demo Button */}
            <div className="mt-4">
              <button
                type="button"
                onClick={enterDemoMode}
                className="w-full py-2 px-4 border border-slate-300 rounded-lg text-slate-600 hover:bg-slate-50 transition-colors text-sm flex items-center justify-center gap-2"
                data-testid="try-demo-btn"
              >
                <Zap className="w-4 h-4" />
                Try Demo (No signup required)
              </button>
            </div>

            {/* Back to Landing */}
            {onBack && (
              <div className="mt-3 text-center">
                <button
                  type="button"
                  onClick={onBack}
                  className="text-sm text-slate-500 hover:text-slate-700 flex items-center justify-center gap-1 mx-auto"
                  data-testid="back-to-landing"
                >
                  <ArrowLeft className="w-3 h-3" />
                  Back to home
                </button>
              </div>
            )}

            {/* Info for signup */}
            {!isLogin && (
              <div className="mt-3 p-2 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-xs text-amber-700">
                  <strong>Note:</strong> New accounts require admin approval.
                </p>
              </div>
            )}

            {/* Trading Disclaimer */}
            <div className="mt-4 p-2 bg-zinc-100 border border-zinc-200 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-[10px] text-zinc-600 leading-relaxed">
                  <strong className="text-zinc-700">Disclaimer:</strong> TradeRelax is for educational and informational purposes only. 
                  This is <strong>NOT financial advice</strong>. Always do your own research (DYOR) before making any investment decisions. 
                  Past performance does not guarantee future results. Trading involves significant risk of loss.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
